/* Policy pages: native content, progressive disclosure, local search and full-text printing. */
(() => {
  'use strict';
  const words = value => value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').split(/\s+/).filter(Boolean);
  const matches = (text, query) => query.every(word => text.includes(word));
  if (!customElements.get('tss-policy-reader')) customElements.define('tss-policy-reader', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) {if (this.items) this.attachWindow(); return;}
      this.ready = true;
      if (this.hasAttribute('data-native-policy')) this.prepareNative();
      const content = this.querySelector('[data-policy-content]');
      if (!content) return;
      const headings = [...content.children].filter(node => node.tagName === 'H2');
      if (!headings.length) return; // Older/plain policies remain readable without enhancement.
      const index = this.querySelector('[data-policy-index]');
      this.items = headings.map((heading, i) => {
        const item = document.createElement('details'); item.className = 'tss-policy-item';
        item.id = heading.id || `policy-${this.querySelector('input').id}-${i + 1}`; heading.removeAttribute('id');
        const summary = document.createElement('summary');
        const body = document.createElement('div'); body.className = 'tss-policy-item-body';
        heading.before(item); summary.append(heading); item.append(summary, body);
        while (item.nextSibling && !(item.nextSibling.nodeType === 1 && item.nextSibling.tagName === 'H2')) body.append(item.nextSibling);
        const link = document.createElement('a'); link.href = `#${item.id}`; link.textContent = heading.textContent; index.append(link);
        item.addEventListener('toggle', () => this.updateExpand());
        return {item, summary, text: words(item.textContent).join(' '), link};
      });
      this.search = this.querySelector('[data-policy-search]');
      this.expand = this.querySelector('[data-policy-expand]');
      this.result = this.querySelector('[data-policy-result]');
      this.querySelector('[data-policy-tools]').hidden = false;
      this.querySelector('[data-policy-sidebar]').hidden = false;
      if (matchMedia('(max-width: 989px)').matches) this.querySelector('.tss-policy-index').open = false;
      this.search.addEventListener('input', () => this.filter());
      const clear = () => {this.search.value = ''; this.filter(); this.search.focus();};
      this.querySelector('[data-policy-clear]').addEventListener('click', clear);
      this.querySelector('[data-policy-reset]').addEventListener('click', clear);
      this.expand.addEventListener('click', () => {const open = this.visible().some(({item}) => !item.open); this.visible().forEach(({item}) => {item.open = open;}); this.updateExpand();});
      this.querySelector('[data-policy-print]').addEventListener('click', () => {this.beforePrint(); window.print();});
      this.addEventListener('click', event => {
        const link = event.target.closest('a[href^="#"]'); if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const record = this.findHash(link.hash); if (!record) return;
        event.preventDefault(); history.pushState(null, '', link.hash); this.reveal(record, true);
      });
      this.onHash = () => {const record = this.findHash(location.hash); if (record) this.reveal(record, false);};
      this.onBeforePrint = () => this.beforePrint();
      this.onAfterPrint = () => this.afterPrint();
      this.attachWindow();
      this.items[0].item.open = true;
      this.updateExpand();
      requestAnimationFrame(this.onHash);
    }
    prepareNative() {
      const container = this.querySelector('.shopify-policy__container');
      const body = container?.querySelector('.shopify-policy__body');
      const template = this.querySelector('template[data-policy-native-ui]');
      if (!body || !template) return;
      const content = body.querySelector(':scope > .rte') || body;
      if (![...content.children].some(node => node.tagName === 'H2')) return;
      // Move the existing nodes: preserve Shopify's markup, forms and listeners.
      const ui = template.content.cloneNode(true);
      content.setAttribute('data-policy-content', '');
      body.classList.add('tss-policy-content');
      ui.querySelector('[data-policy-content-slot]').replaceWith(body);
      container.append(ui);
      template.remove();
    }
    attachWindow() {window.addEventListener('hashchange', this.onHash); window.addEventListener('beforeprint', this.onBeforePrint); window.addEventListener('afterprint', this.onAfterPrint);}
    disconnectedCallback() {window.removeEventListener('hashchange', this.onHash); window.removeEventListener('beforeprint', this.onBeforePrint); window.removeEventListener('afterprint', this.onAfterPrint);}
    visible() {return this.items.filter(({item}) => !item.hidden);}
    updateExpand() {if (this.expand) this.expand.textContent = this.visible().every(({item}) => item.open) ? 'Collapse all' : 'Expand all';}
    findHash(hash) {let id; try {id = decodeURIComponent(hash.slice(1));} catch (_) {return null;} return this.items.find(({item}) => item.id === id || [...item.querySelectorAll('[id]')].some(node => node.id === id));}
    reveal(record, focus) {this.search.value = ''; this.filter(); record.item.open = true; record.item.scrollIntoView({block: 'start', behavior: 'auto'}); if (focus) record.summary.focus({preventScroll: true}); this.updateExpand();}
    filter() {
      const query = words(this.search.value);
      if (query.length && !this.previousOpen) this.previousOpen = this.items.map(({item}) => item.open);
      this.items.forEach(({item, text, link}, i) => {const found = matches(text, query); item.hidden = !found; link.hidden = !found; if (query.length && found) item.open = true; else if (!query.length && this.previousOpen) item.open = this.previousOpen[i];});
      if (!query.length) this.previousOpen = null;
      const count = this.visible().length;
      this.result.textContent = query.length ? `${count} of ${this.items.length} sections match. Clear the search to read the full policy.` : '';
      this.querySelector('[data-policy-clear]').hidden = !query.length;
      this.querySelector('[data-policy-empty]').hidden = count !== 0;
      this.expand.disabled = count === 0;
      this.updateExpand();
    }
    beforePrint() {if (this.printState || !this.items) return; this.printState = this.items.map(({item}) => ({open:item.open, hidden:item.hidden})); this.items.forEach(({item}) => {item.hidden = false; item.open = true;});}
    afterPrint() {if (!this.printState) return; this.items.forEach(({item}, i) => Object.assign(item, this.printState[i])); this.printState = null; this.updateExpand();}
  });
  if (!customElements.get('tss-policy-directory')) customElements.define('tss-policy-directory', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      const cards = [...this.querySelectorAll('[data-policy-card]')].map(node => ({node, text:words(node.textContent + ' ' + node.dataset.keywords).join(' ')}));
      const input = this.querySelector('[data-directory-search]'), clear = this.querySelector('[data-directory-clear]');
      this.querySelector('[data-directory-tools]').hidden = false;
      const filter = () => {const query = words(input.value); cards.forEach(({node, text}) => {node.hidden = !matches(text, query);}); const count = cards.filter(({node}) => !node.hidden).length; this.querySelector('[data-directory-result]').textContent = query.length ? `${count} ${count === 1 ? 'policy' : 'policies'} found` : ''; this.querySelector('[data-directory-empty]').hidden = count !== 0; clear.hidden = !query.length;};
      input.addEventListener('input', filter); clear.addEventListener('click', () => {input.value = ''; filter(); input.focus();});
    }
  });
})();
